import { useEffect, useRef, useState } from 'react';

import type { TFunction } from 'i18next';
import type { RefObject } from 'react';

// Con `maxComments` la lista enseña solo los primeros y «Ver todos los comentarios» despliega el resto.
const VISIBLE_COMMENTS = 2;
// Solo los <li> de la propia lista de comentarios: `:scope >` deja fuera los de una lista anidada en un comentario.
const COMMENT_ITEMS_SELECTOR = ':scope > .comments-list > .basic-list > .basic-list__item';

type Params<TComment> = Readonly<{
  comments: TComment[] | null;
  maxComments: boolean;
  t: TFunction;
}>;

type UseCommentsListAndCreate<TComment> = Readonly<{
  hasComments: boolean;
  isViewAllVisible: boolean;
  listRef: RefObject<HTMLDivElement>;
  onShowAll: () => void;
  viewAllLabel: string;
  visibleComments: TComment[];
}>;

function useCommentsListAndCreate<TComment>({ comments, maxComments, t }: Params<TComment>): UseCommentsListAndCreate<TComment> {
  const [showAll, setShowAll] = useState<boolean>(!maxComments);
  const listRef = useRef<HTMLDivElement>(null);
  const isRevealRequestedRef = useRef<boolean>(false);

  const allComments = comments ?? [];
  const hiddenCount = allComments.length - VISIBLE_COMMENTS;
  const isViewAllVisible = maxComments && hiddenCount > 0 && !showAll;
  const visibleComments = showAll ? allComments : allComments.slice(0, VISIBLE_COMMENTS);

  // Al desplegar, el botón desaparece y el foco caería en <body> (WCAG 2.4.3, TAN-11221): pasa al
  // primer comentario que aparece. BasicList solo da tabIndex -1 al destino del deep link (TAN-11146).
  useEffect(() => {
    if (!showAll || !isRevealRequestedRef.current) return;
    isRevealRequestedRef.current = false;
    const firstRevealed = listRef.current?.querySelectorAll<HTMLLIElement>(COMMENT_ITEMS_SELECTOR)[VISIBLE_COMMENTS];
    firstRevealed?.setAttribute('tabindex', '-1');
    firstRevealed?.addEventListener('blur', () => firstRevealed.removeAttribute('tabindex'), { once: true });
    firstRevealed?.focus();
  }, [showAll]);

  function onShowAll(): void {
    isRevealRequestedRef.current = true;
    setShowAll(true);
  }

  return {
    hasComments: allComments.length > 0,
    isViewAllVisible,
    listRef,
    onShowAll,
    viewAllLabel: `${t('common:View all comments')} (${hiddenCount})`,
    visibleComments,
  };
}

export default useCommentsListAndCreate;
